import { useState, useEffect } from 'react';
import {
  fetchDataSourcesList,
  fetchDataSourcesListWithSearch,
  fetchAnswersList,
  fetchAnswersListWithSearch,
} from '../../../../../services/api';
import { useLiveboardSearch } from '../../../../../hooks/useLiveboardSearch';
import { useVizSearch } from '../../../../../hooks/useVizSearch';
import { DropdownOption } from '..';

import { useVariable } from 'context/ThemeContext';
import {
  COMPONENT_FULL_APP_SELECTION_OPTIONS,
  COMPONENT_SELECTION_OPTIONS,
} from '../../../../../constants';
import { getInfoObject } from 'utils/tokenService';
import { Page } from '@thoughtspot/visual-embed-sdk';

export type DropdownType =
  | 'datasource'
  | 'answer'
  | 'liveboard'
  | 'visualization'
  | 'fullapp'
  | 'component-selection';

interface UseDropdownDataProps {
  type: DropdownType;
  searchPattern?: string;
  liveboardId?: string;
}

export const useDropdownData = ({
  type,
  searchPattern = '',
  liveboardId,
}: UseDropdownDataProps) => {
  const [options, setOptions] = useState<DropdownOption[]>([]);
  const [loading, setLoading] = useState(false);
  const { dispatch } = useVariable();
  // Only use liveboard and viz hooks when actually needed
  const { data: liveboardData, loading: liveboardLoading } = useLiveboardSearch(
    type === 'liveboard' ? searchPattern : ''
  );
  const { data: vizData, loading: vizLoading } = useVizSearch(
    type === 'visualization' ? liveboardId || '' : '',
    type === 'visualization' ? searchPattern : ''
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        let result;

        switch (type) {
          case 'datasource': {
            if (!searchPattern.trim()) {
              result = await fetchDataSourcesList();
            } else {
              result = await fetchDataSourcesListWithSearch(searchPattern);
            }

            const sourcesList = result?.objects || [];
            dispatch({ type: 'updateDataSourceList', value: sourcesList });

            setOptions(
              sourcesList
                .map((ele: any) => ({
                  id: ele.id,
                  label: ele.name,
                }))
                .filter(
                  (row: DropdownOption) =>
                    !searchPattern.trim() ||
                    row.label?.toLowerCase().includes(searchPattern.toLowerCase())
                )
            );
            break;
          }

          case 'answer': {
            if (!searchPattern.trim()) {
              result = await fetchAnswersList();
            } else {
              result = await fetchAnswersListWithSearch(searchPattern);
            }
            const answersList = result?.objects || [];
            dispatch({ type: 'updateAnswerList', value: answersList });
            setOptions(
              answersList
                .map((ele: any) => ({
                  id: ele.id,
                  label: ele.name,
                }))
                .filter(
                  (row: DropdownOption) =>
                    !searchPattern.trim() ||
                    row.label?.toLowerCase().includes(searchPattern.toLowerCase())
                )
            );
            break;
          }

          case 'liveboard':
            setOptions(
              liveboardData.length > 0
                ? liveboardData.map((ele: any) => ({
                    id: ele.id,
                    label: ele.name,
                  }))
                : searchPattern.trim() !== ''
                ? [{ id: 'no-results', label: 'No liveboards found', disabled: true }]
                : []
            );
            break;

          case 'visualization':
            setOptions(
              vizData.length > 0
                ? vizData
                    .map((viz: any) => ({
                      id: viz.id,
                      label: `${viz.name} (${viz.type})`,
                    }))
                    .sort((a: any, b: any) => a.label.localeCompare(b.label))
                : searchPattern.trim() !== '' && liveboardId
                ? [{ id: 'no-results', label: 'No visualizations found', disabled: true }]
                : []
            );
            break;

          case 'fullapp': {
            const fullAppOptions = [...COMPONENT_FULL_APP_SELECTION_OPTIONS];
            try {
              const infoObject = (await getInfoObject()) as any;
              console.log(
                'infoObject',
                infoObject?.info?.configInfo?.a3Config?.a3LiveDataAnalysisEnabled,
                infoObject?.info?.privileges,
                infoObject?.info?.privileges.includes('A3ANALYSIS')
              );
              const spotIQEnabled =
                infoObject?.info?.configInfo?.a3Config?.a3LiveDataAnalysisEnabled &&
                infoObject?.info?.privileges?.includes('A3ANALYSIS');
              if (spotIQEnabled && !fullAppOptions.some((option) => option.id === Page.SpotIQ)) {
                fullAppOptions.push({ id: Page.SpotIQ, label: 'SpotIQ', disabled: false });
              }
            } catch (error) {
              // If getInfoObject fails, just use default options
              console.warn('Failed to get infoObject for fullapp options:', error);
            }

            if (searchPattern.trim()) {
              setOptions(
                fullAppOptions.filter((option) =>
                  option.label.toLowerCase().includes(searchPattern.toLowerCase())
                )
              );
            } else {
              setOptions(fullAppOptions);
            }
            break;
          }

          case 'component-selection':
            setOptions(COMPONENT_SELECTION_OPTIONS);
            break;

          default:
            setOptions([]);
        }
      } catch (error) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [type, searchPattern, liveboardId, liveboardData, vizData]);

  // For liveboard and visualization, use the loading state from hooks
  const isLoading =
    type === 'liveboard' ? liveboardLoading : type === 'visualization' ? vizLoading : loading;

  return { options, loading: isLoading };
};
